import {
  compressProfileAvatarDataUrlForUpload,
  estimateDataUrlBytes,
} from './profileAvatarUpload';

/**
 * Profile PATCH may send only the logo field; decoded-byte budget is fine there.
 * Asset-owner register sends logo inside a larger JSON body — use wire-byte helpers below.
 */
export const OWNER_LOGO_UPLOAD_MAX_BYTES = 350_000;

/**
 * Default API host caps whole JSON POST bodies near ~100 KB (see fetchWithDevProxyFallback).
 * Budget the logo against the full register payload, not the file size on disk.
 */
export const OWNER_LOGO_REGISTER_JSON_MAX_BYTES = 96_000;

/** Conservative logo wire budget at pick time before bank + password fields are final. */
export const OWNER_LOGO_REGISTER_PICK_MAX_WIRE_BYTES = 52_000;

/** Longest canvas edge when re-encoding logos for upload. */
export const OWNER_LOGO_UPLOAD_MAX_EDGE = 1024;

/** Allow picking large source files; they are re-encoded before upload. */
export const OWNER_LOGO_PICK_MAX_FILE_BYTES = 12 * 1024 * 1024;

type LogoByteMeasure = 'decoded' | 'wire';

function utf8ByteLength(value: string): number {
  if (typeof globalThis.TextEncoder !== 'undefined') {
    return new globalThis.TextEncoder().encode(value).length;
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.byteLength(value, 'utf8');
  }
  return value.length;
}

export function estimateDataUrlWireUtf8Bytes(dataUrl: string): number {
  return utf8ByteLength(dataUrl);
}

export function estimateJsonBodyUtf8Bytes(value: unknown): number {
  try {
    const encoded = JSON.stringify(value);
    if (encoded === undefined) {
      return 0;
    }
    return utf8ByteLength(encoded);
  } catch {
    return Number.MAX_SAFE_INTEGER;
  }
}

function measureLogoDataUrlBytes(dataUrl: string, byteMeasure: LogoByteMeasure): number {
  return byteMeasure === 'wire' ? estimateDataUrlWireUtf8Bytes(dataUrl) : estimateDataUrlBytes(dataUrl);
}

export function ownerLogoDataUrlWithinUploadBudget(dataUrl: string): boolean {
  return estimateDataUrlBytes(dataUrl) <= OWNER_LOGO_UPLOAD_MAX_BYTES;
}

export function ownerLogoDataUrlWithinWireBudget(dataUrl: string, maxWireBytes: number): boolean {
  return estimateDataUrlWireUtf8Bytes(dataUrl) <= maxWireBytes;
}

export function ownerLogoMaxWireBytesForRegisterBody(
  registerBodyWithoutLogo: Record<string, unknown>,
  jsonMaxBytes: number = OWNER_LOGO_REGISTER_JSON_MAX_BYTES,
): number {
  const reserved = estimateJsonBodyUtf8Bytes(registerBodyWithoutLogo);
  const margin = 2_000;
  return Math.max(10_000, jsonMaxBytes - reserved - margin);
}

export function assetOwnerRegisterPostWithinJsonLimit(
  body: Record<string, unknown>,
  jsonMaxBytes: number = OWNER_LOGO_REGISTER_JSON_MAX_BYTES,
): boolean {
  return estimateJsonBodyUtf8Bytes(body) <= jsonMaxBytes;
}

export async function prepareOwnerLogoDataUrlForUpload(dataUrl: string): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }
  if (
    dataUrl.startsWith('data:image/jpeg') &&
    ownerLogoDataUrlWithinUploadBudget(dataUrl)
  ) {
    return dataUrl;
  }
  return compressOwnerLogoDataUrlForUpload(dataUrl);
}

/** Always re-encode and fit the logo to a register JSON wire-byte budget (strips EXIF/ICC). */
export async function prepareOwnerLogoForAssetOwnerRegister(
  dataUrl: string,
  maxWireBytes: number,
): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }
  const compressed = await compressOwnerLogoDataUrlForUpload(dataUrl, maxWireBytes, 'wire');
  if (!ownerLogoDataUrlWithinWireBudget(compressed, maxWireBytes)) {
    throw new Error('OWNER_LOGO_REGISTER_TOO_LARGE');
  }
  return compressed;
}

async function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Could not load business logo'));
    image.src = dataUrl;
  });
  return image;
}

function canvasToJpegDataUrl(canvas: HTMLCanvasElement, quality: number): string | null {
  return canvas.toDataURL('image/jpeg', quality);
}

/** Re-encode logos to JPEG with a larger max edge than avatars; always shrinks PNG/WebP picks. */
export async function compressOwnerLogoDataUrlForUpload(
  dataUrl: string,
  maxBytes: number = OWNER_LOGO_UPLOAD_MAX_BYTES,
  byteMeasure: LogoByteMeasure = 'decoded',
): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }

  const image = await loadImageFromDataUrl(dataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return compressProfileAvatarDataUrlForUpload(dataUrl, maxBytes);
  }

  let edge = Math.min(OWNER_LOGO_UPLOAD_MAX_EDGE, Math.max(image.width, image.height));
  let best = dataUrl;

  try {
    for (let shrink = 0; shrink < 10; shrink += 1) {
      const scale = edge / Math.max(image.width, image.height);
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(image, 0, 0, width, height);

      for (let quality = 0.9; quality >= 0.4; quality -= 0.05) {
        const candidate = canvasToJpegDataUrl(canvas, quality);
        if (!candidate) {
          continue;
        }
        if (measureLogoDataUrlBytes(candidate, byteMeasure) <= maxBytes) {
          return candidate;
        }
        best = candidate;
      }

      edge = Math.max(64, Math.round(edge * 0.78));
    }
  } catch {
    return compressProfileAvatarDataUrlForUpload(dataUrl, maxBytes);
  }

  return best;
}
